import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { AccountDeleted, AccountDeletion } from '@/api/account'
import type { SessionDescription } from '@/api/auth'
import { ApiError, apiFetch, onReassertionRequired, onSignedOut } from '~/api/client'

/**
 * The client half of `src/api/auth.ts`. Two things about that contract shape
 * everything here: every ceremony's options arrive wrapped in `publicKey` with
 * their binary fields base64url, and every request body is `.strict()` — a
 * payload carrying so much as the `rawId` the browser also offers is rejected
 * outright. So a credential is narrowed to exactly the fields the server parses
 * rather than forwarded as the authenticator returned it.
 */
export const SESSION_KEY = ['auth', 'session'] as const

/**
 * Retiring an account, rewriting a valuation, destroying a year-end pin,
 * rotating a source's credential: the server refuses all of them on a session
 * nobody has touched a key for in the last few minutes.
 *
 * Installed here because this is the module that knows how to hold a ceremony,
 * and installed on import rather than from a component because a household in
 * the middle of one of those acts is not going to be told to reload the page
 * first. Everything the app sends goes through `apiFetch`, so this is the whole
 * of the client's answer — no page has to know the rule exists.
 */
onReassertionRequired(async () => {
  const { publicKey } = await apiFetch<{ publicKey: RequestOptionsJson }>(
    '/auth/reassert/options',
    post({}),
  )
  await apiFetch('/auth/reassert', post({ credential: await requestAssertion(publicKey) }))
})

/** The session is the whole account: who, which households, and every passkey. */
export type Session = SessionDescription
export type Membership = Session['tenants'][number]
export type TenantRole = Membership['role']
export type CredentialRecord = Session['credentials'][number]

export type SessionResult = { state: 'authenticated'; session: Session } | { state: 'anonymous' }

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous'

export interface SessionState {
  status: SessionStatus
  session: Session | null
  error: Error | null
}

export function useSession(): SessionState {
  const client = useQueryClient()

  const query = useQuery({
    queryKey: SESSION_KEY,
    queryFn: fetchSession,
    retry: false,
    // The server expires a session on idleness alone, so a page that treated
    // the answer as fresh for five minutes would go on rendering a household
    // it can no longer read.
    staleTime: 0,
    // On for this query alone. Coming back to a tab left open is when a session
    // is most likely to have gone and the cheapest moment to find out, whereas
    // the figures behind it move once a night and refetching all of them on
    // every tab switch would buy nothing.
    refetchOnWindowFocus: true,
  })

  // Nothing else re-runs this while somebody navigates inside the app: the
  // component stays mounted, so the answer on screen is the one from sign-in
  // however long ago that was.
  useEffect(
    () =>
      onSignedOut(() => {
        client.setQueryData<SessionResult>(SESSION_KEY, { state: 'anonymous' })
      }),
    [client],
  )

  return {
    status: query.data?.state ?? 'loading',
    session: query.data?.state === 'authenticated' ? query.data.session : null,
    error: query.error,
  }
}

async function fetchSession(): Promise<SessionResult> {
  try {
    return { state: 'authenticated', session: await apiFetch<Session>('/auth/session') }
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return { state: 'anonymous' }
    throw error
  }
}

export function useLogin(): UseMutationResult<Session, Error, { email: string }> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const { publicKey } = await apiFetch<{ publicKey: RequestOptionsJson }>(
        '/auth/login/options',
        post({ email }),
      )
      const credential = await requestAssertion(publicKey)
      return apiFetch<Session>('/auth/login', post({ email, credential }))
    },
    onSuccess: adopt,
  })
}

export interface RegisterInput {
  email: string
  name?: string
  /** The token out of the invitation link; absent when claiming an empty install. */
  invitation?: string
}

export function useRegister(): UseMutationResult<Session, Error, RegisterInput> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: async (input: RegisterInput) => {
      const { publicKey } = await apiFetch<{ publicKey: CreationOptionsJson }>(
        '/auth/register/options',
        post({ email: input.email, ...(input.invitation ? { invitation: input.invitation } : {}) }),
      )
      const credential = await createCredential(publicKey)
      return apiFetch<Session>('/auth/register', post({ ...input, credential }))
    },
    onSuccess: adopt,
  })
}

export function useSwitchTenant(): UseMutationResult<Session, Error, number> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: (tenantId: number) => apiFetch<Session>('/auth/tenant', post({ tenantId })),
    onSuccess: adopt,
  })
}

/**
 * Creating a household opens it, so the session that comes back is already about
 * different money and is adopted exactly like a switch.
 */
export function useCreateTenant(): UseMutationResult<Session, Error, { name: string }> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: ({ name }: { name: string }) => apiFetch<Session>('/auth/tenants', post({ name })),
    onSuccess: adopt,
  })
}

/**
 * An account can own one household and merely belong to another, and every
 * invitation route reads the household off the session — so the role that
 * governs what may be done is the role in the household currently open, never
 * the strongest role held anywhere.
 */
export function activeMembership(session: Session): Membership | undefined {
  return session.tenants.find((membership) => membership.tenantId === session.tenantId)
}

/** An invitation that has been written and not yet accepted. Never carries the token. */
export interface PendingInvitation {
  email: string
  role: TenantRole
  createdAt: string
  expiresAt: string
}

export interface IssuedInvitation {
  token: string
  email: string
  role: TenantRole
}

const INVITATIONS_KEY = ['auth', 'invitations'] as const

/**
 * Answered 403 for anyone but the owner of the open household, so it is mounted
 * only for one and a refusal is not worth asking again.
 */
export function useInvitations(): UseQueryResult<{ invitations: PendingInvitation[] }> {
  return useQuery({
    queryKey: INVITATIONS_KEY,
    queryFn: () => apiFetch<{ invitations: PendingInvitation[] }>('/auth/invitations'),
    retry: false,
  })
}

export function useInvite(): UseMutationResult<
  IssuedInvitation,
  Error,
  { email: string; role: TenantRole }
> {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { email: string; role: TenantRole }) =>
      apiFetch<IssuedInvitation>('/auth/invitations', post(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: INVITATIONS_KEY })
    },
  })
}

/**
 * The other half of `useInvite`, for a visitor who already had somewhere to
 * sign in to: registering enrols a passkey and redeems the token in the same
 * request, but signing in does neither, so this is the call that actually
 * moves them into the household afterwards.
 */
export function useAcceptInvitation(): UseMutationResult<Session, Error, { token: string }> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: ({ token }: { token: string }) =>
      apiFetch<Session>('/auth/invitations/accept', post({ token })),
    onSuccess: adopt,
  })
}

/**
 * A token is only of use as a link. `Login` reads it back off `?invitation=` on
 * this origin, and the two halves live together so a change to one cannot leave
 * senders handing out addresses the sign-in page does not look at.
 */
export function invitationLink(token: string): string {
  return `${window.location.origin}/?invitation=${encodeURIComponent(token)}`
}

export function invitationFromLocation(): string | undefined {
  const invitation = new URLSearchParams(window.location.search).get('invitation')
  return invitation === null || invitation === '' ? undefined : invitation
}

/**
 * Read once, acted on once. Called after the token has been spent one way or
 * another — accepted, declined, or already redeemed by registering — so a
 * reload of the same address does not offer it again.
 */
export function clearInvitationFromLocation(): void {
  const url = new URL(window.location.href)
  url.searchParams.delete('invitation')
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

export function useSignOut(): UseMutationResult<unknown, Error, void> {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => apiFetch<unknown>('/auth/logout', post({})),
    onSuccess: () => {
      forgetEveryFigure(client)
      client.setQueryData<SessionResult>(SESSION_KEY, () => ({ state: 'anonymous' }))
    },
  })
}

export function useAddCredential(): UseMutationResult<Session, Error, { name?: string }> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: async ({ name }: { name?: string }) => {
      const { publicKey } = await apiFetch<{ publicKey: CreationOptionsJson }>(
        '/auth/credentials/options',
        post({}),
      )
      const credential = await createCredential(publicKey)
      return apiFetch<Session>('/auth/credentials', post({ ...(name ? { name } : {}), credential }))
    },
    onSuccess: adopt,
  })
}

/** Keyed by the authenticator's own credential id, which is what the route takes. */
export function useRemoveCredential(): UseMutationResult<Session, Error, string> {
  const adopt = useAdoptSession()

  return useMutation({
    mutationFn: (credentialId: string) =>
      apiFetch<Session>(`/auth/credentials/${encodeURIComponent(credentialId)}`, {
        method: 'DELETE',
      }),
    onSuccess: adopt,
  })
}

/** What `GET /api/account` answers: what deleting this account would take. */
export type AccountDeletionPlan = AccountDeletion
export type HouseholdAtStake = AccountDeletion['households'][number]
export type SharedHousehold = AccountDeletion['blocked'][number]
export type AccountDeletionOutcome = AccountDeleted

const DELETION_KEY = ['auth', 'deletion'] as const

/**
 * What would go, asked before anything does.
 *
 * Fetched rather than assembled from the session, because the session knows the
 * households and not what is in them — and a confirmation that cannot say what
 * it is about to destroy is a confirmation nobody can give properly.
 */
export function useAccountDeletion(enabled: boolean): UseQueryResult<AccountDeletionPlan> {
  return useQuery({
    queryKey: DELETION_KEY,
    queryFn: () => apiFetch<AccountDeletionPlan>('/account'),
    retry: false,
    staleTime: 0,
    // Counting every row of every household is not what somebody opening the
    // account page asked for, so it is asked once they have said they are
    // thinking about this.
    enabled,
  })
}

/**
 * The one irreversible act. The passkey is asked for on the way by `apiFetch`,
 * which holds that ceremony for every call that meets the refusal, and what
 * comes back is an account that no longer exists — so the session is dropped
 * here rather than refetched.
 */
export function useDeleteAccount(): UseMutationResult<
  AccountDeletionOutcome,
  Error,
  { confirm: string }
> {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ confirm }: { confirm: string }) =>
      apiFetch<AccountDeletionOutcome>('/account', {
        method: 'DELETE',
        body: JSON.stringify({ confirm }),
      }),
    onSuccess: () => {
      forgetEveryFigure(client)
      client.setQueryData<SessionResult>(SESSION_KEY, () => ({ state: 'anonymous' }))
    },
  })
}

/**
 * A table name in the words of somebody who has never seen the schema.
 *
 * The server counts what it is about to remove per table, which is the only
 * honest account of it — but `account_report: 412` is not a sentence anybody can
 * weigh a decision against. An unmapped name is printed rather than dropped: a
 * table this build has not heard of is still something about to be destroyed.
 */
const HOLDING_LABEL: Record<string, string> = {
  account: 'accounts',
  account_report: 'source reports',
  cashflow: 'money movements',
  connector_state: 'stored connections',
  entity: 'people and companies',
  instrument: 'instruments',
  invitation: 'invitations',
  ownership: 'ownership links',
  position: 'daily positions',
  price: 'daily prices',
  price_override: 'prices entered by hand',
  setting: 'settings',
  snapshot_run: 'nightly runs',
  source_result: 'source results',
  tenant_key: 'encryption keys',
  tenant_member: 'members',
  unit: 'units',
  valuation_event: 'valuations',
  warrant_grant: 'warrant grants',
}

export interface Holding {
  label: string
  rows: number
}

/** What a household holds, largest first, so the sentence leads with the weight. */
export function holdingsOf(holds: Readonly<Record<string, number>>): Holding[] {
  return Object.entries(holds)
    .map(([table, rows]) => ({ label: HOLDING_LABEL[table] ?? table.replace(/_/g, ' '), rows }))
    .sort((a, b) => b.rows - a.rows || a.label.localeCompare(b.label))
}

function useAdoptSession(): (session: Session) => void {
  const client = useQueryClient()

  return (session: Session) => {
    forgetEveryFigure(client)
    client.setQueryData<SessionResult>(SESSION_KEY, () => ({ state: 'authenticated', session }))
  }
}

/**
 * Signing in, signing out and switching household all change whose money the
 * pages are about, and a refetch leaves the previous household's tiles on screen
 * until it lands. Emptying the cache costs a loading state; not emptying it
 * shows one family their figures under another's name. The session itself is
 * spared: it is not stale data, it is the answer that replaced it.
 */
function forgetEveryFigure(client: QueryClient): void {
  client.removeQueries({
    predicate: (query) =>
      query.queryKey[0] !== SESSION_KEY[0] || query.queryKey[1] !== SESSION_KEY[1],
  })
}

export type CredentialBinding = 'synced' | 'device-bound'

/**
 * WebAuthn's backup-eligible flag is the only thing separating a key the
 * platform will copy to the holder's other devices from one that dies with the
 * device holding it. An authenticator that reported nothing is stored as false,
 * so "device-bound" here means "not known to be recoverable" — the only safe
 * reading, because a passkey wrongly believed to be synced is exactly the one
 * nobody makes a second copy of.
 */
export function bindingOf(credential: CredentialRecord): CredentialBinding {
  return credential.backupEligible ? 'synced' : 'device-bound'
}

export type CredentialStanding = 'none' | 'all-device-bound' | 'recoverable'

/**
 * Whether this account has a way back in. The lockout risk is the server's own
 * answer rather than a second opinion derived from the same flags, so what the
 * page warns about and what the API knows cannot drift apart.
 */
export function standingOf(session: Session): CredentialStanding {
  if (session.credentials.length === 0) return 'none'
  return session.atRiskOfLockout ? 'all-device-bound' : 'recoverable'
}

/** Stored comma-joined, because the column holds what the browser said, once. */
export function transportsOf(credential: CredentialRecord): string[] {
  return credential.transports?.split(',').filter((transport) => transport !== '') ?? []
}

export function hasAuthenticator(target: Navigator | undefined = globalThis.navigator): boolean {
  const credentials = target?.credentials
  return typeof credentials?.create === 'function' && typeof credentials.get === 'function'
}

export interface PublicKeyCredentialDescriptorJson {
  id: string
  type: 'public-key'
  transports?: AuthenticatorTransport[]
}

export interface CreationOptionsJson {
  challenge: string
  rp: { id?: string; name: string }
  user: { id: string; name: string; displayName: string }
  pubKeyCredParams: { type: 'public-key'; alg: number }[]
  timeout?: number
  attestation?: AttestationConveyancePreference
  authenticatorSelection?: AuthenticatorSelectionCriteria
  excludeCredentials?: PublicKeyCredentialDescriptorJson[]
}

export interface RequestOptionsJson {
  challenge: string
  rpId?: string
  timeout?: number
  userVerification?: UserVerificationRequirement
  allowCredentials?: PublicKeyCredentialDescriptorJson[]
}

export interface AttestationPayload {
  id: string
  response: {
    clientDataJSON: string
    attestationObject: string
    transports?: string[]
  }
}

export interface AssertionPayload {
  id: string
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
  }
}

interface AttestationLike {
  id: string
  response: {
    clientDataJSON: ArrayBuffer
    attestationObject: ArrayBuffer
    getTransports?: () => string[]
  }
}

interface AssertionLike {
  id: string
  response: {
    clientDataJSON: ArrayBuffer
    authenticatorData: ArrayBuffer
    signature: ArrayBuffer
  }
}

export class AuthenticatorError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthenticatorError'
  }
}

export async function createCredential(options: CreationOptionsJson): Promise<AttestationPayload> {
  const created = (await requireAuthenticator().create({
    publicKey: toCreationOptions(options),
  })) as AttestationLike | null
  if (!created) throw new AuthenticatorError('the authenticator returned no credential')
  return serialiseAttestation(created)
}

export async function requestAssertion(options: RequestOptionsJson): Promise<AssertionPayload> {
  const asserted = (await requireAuthenticator().get({
    publicKey: toRequestOptions(options),
  })) as AssertionLike | null
  if (!asserted) throw new AuthenticatorError('the authenticator returned no credential')
  return serialiseAssertion(asserted)
}

function requireAuthenticator(): CredentialsContainer {
  if (!hasAuthenticator()) throw new AuthenticatorError('this browser cannot use passkeys')
  return globalThis.navigator.credentials
}

export function toCreationOptions(options: CreationOptionsJson): PublicKeyCredentialCreationOptions {
  return {
    ...options,
    challenge: decodeBase64Url(options.challenge),
    user: { ...options.user, id: decodeBase64Url(options.user.id) },
    excludeCredentials: options.excludeCredentials?.map(toDescriptor),
  }
}

export function toRequestOptions(options: RequestOptionsJson): PublicKeyCredentialRequestOptions {
  return {
    ...options,
    challenge: decodeBase64Url(options.challenge),
    allowCredentials: options.allowCredentials?.map(toDescriptor),
  }
}

function toDescriptor(descriptor: PublicKeyCredentialDescriptorJson): PublicKeyCredentialDescriptor {
  return {
    id: decodeBase64Url(descriptor.id),
    type: descriptor.type,
    transports: descriptor.transports,
  }
}

export function serialiseAttestation(credential: AttestationLike): AttestationPayload {
  // Registration is the only moment the browser will name the transports, and
  // they are what later tells one identical-looking key from another.
  const transports = credential.response.getTransports?.()

  return {
    id: credential.id,
    response: {
      clientDataJSON: encodeBase64Url(credential.response.clientDataJSON),
      attestationObject: encodeBase64Url(credential.response.attestationObject),
      ...(transports && transports.length > 0 ? { transports } : {}),
    },
  }
}

export function serialiseAssertion(credential: AssertionLike): AssertionPayload {
  return {
    id: credential.id,
    response: {
      clientDataJSON: encodeBase64Url(credential.response.clientDataJSON),
      authenticatorData: encodeBase64Url(credential.response.authenticatorData),
      signature: encodeBase64Url(credential.response.signature),
    },
  }
}

export function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const standard = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(standard.padEnd(standard.length + ((4 - (standard.length % 4)) % 4), '='))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

export function encodeBase64Url(source: ArrayBuffer | Uint8Array): string {
  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** SQLite's `CURRENT_TIMESTAMP` is space-separated, which `describeAge` cannot read. */
export function dayOf(timestamp: string | null): string | null {
  const day = timestamp?.slice(0, 10)
  return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

function post(body: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(body) }
}

export type SignupMode = 'open' | 'invite'

const REGISTRATION_KEY = ['auth', 'registration'] as const

/**
 * Whether this deployment lets a stranger sign up. Asked of the server rather
 * than guessed from whether sign-in failed, so the page offers what is actually
 * on offer instead of inviting somebody into a refusal.
 */
export function useRegistration(): UseQueryResult<{ signup: SignupMode }> {
  return useQuery({
    queryKey: REGISTRATION_KEY,
    queryFn: () => apiFetch<{ signup: SignupMode }>('/auth/registration'),
    staleTime: Infinity,
  })
}
